import type { Meeting } from '@app/application/client_meetings/types'
import type { Remark } from '@app/application/communication/types'
import type { ComputedRef, Ref } from 'vue'
import { computed } from 'vue'
import { useStore } from 'vuex'

interface StoreState {
  remarks: { remarks: Record<string, Remark> }
}

interface UseMeetingRemarksReturn {
  remarks: ComputedRef<Remark[]>
  orderedRemarks: ComputedRef<Remark[]>
  discussedRemarks: ComputedRef<Remark[]>
  postMeetingRemarks: ComputedRef<Remark[]>
}

export const useMeetingRemarks = (
  meeting: Ref<Meeting>
): UseMeetingRemarksReturn => {
  const store = useStore<StoreState>()
  const remarks = computed(() => store.state.remarks.remarks)

  const meetingRemarks = computed(() => {
    const orderedRemarkIds = orderedRemarks.value.map((r) => r.id)
    const unorderedMeetingRemarks = Object.values(remarks.value).filter(
      (remark) =>
        !orderedRemarkIds.includes(remark.id) &&
        remark.clientMeetingId === meeting.value.id
    )

    return orderedRemarks.value.concat(unorderedMeetingRemarks)
  })

  const orderedRemarks = computed(
    () =>
      meeting.value.order
        .map((item) => {
          const [type, id] = item.split('|')
          return type === 'Remark' ? remarks.value[id] : null
        })
        .filter(Boolean) as Remark[]
  )

  // discussedTodoIds is snapshotted at meeting completion (before any
  // follow-up proposal is created), so it's the source of truth regardless
  // of proposal state.
  const discussedRemarks = computed(() =>
    meetingRemarks.value.filter(
      (remark) =>
        !remark.todo ||
        (meeting.value.discussedTodoIds || []).includes(remark.id)
    )
  )

  const postMeetingRemarks = computed(() =>
    meetingRemarks.value.filter(
      (remark) =>
        remark.todo &&
        !(meeting.value.discussedTodoIds || []).includes(remark.id)
    )
  )

  return {
    remarks: meetingRemarks,
    orderedRemarks,
    discussedRemarks,
    postMeetingRemarks,
  }
}
